// (c) 2026 Planet Q Productions. All Rights Reserved.

import React, { useRef, useEffect } from 'react';

const OpenWorldMode = ({
  backgroundImage,
  characterImage,
  enemyImage,
  gameTitle,
  isPlaying,
  onScoreUpdate,
  onGameOver,
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const imagesRef = useRef({});

  useEffect(() => {
    const loadImg = (src) => new Promise(res => {
      if (!src) { res(null); return; }
      const img = new Image();
      if (src.startsWith('http')) img.crossOrigin = 'anonymous';
      img.onload = () => res(img); img.onerror = () => res(null);
      img.src = src;
    });
    Promise.all([
      loadImg(backgroundImage), loadImg(characterImage), loadImg(enemyImage),
    ]).then(([bg, ch, en]) => { imagesRef.current = { bg, ch, en }; });
  }, [backgroundImage, characterImage, enemyImage]);

  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const width = canvas.width = canvas.offsetWidth;
    const height = canvas.height = canvas.offsetHeight;

    const s = {
      frame: 0, score: 0,
      camX: 0, camY: 0,
      playerX: width / 2, playerY: height / 2,
      playerDir: 0, walkAnim: 0, idle: true,
      enemies: [], items: [],
      gameOver: false, lives: 3, invincible: 0,
      particles: [],
    };

    // Scatter items
    for (let i = 0; i < 15; i++) {
      s.items.push({
        x: (Math.random() - 0.5) * 2000,
        y: (Math.random() - 0.5) * 2000,
        type: Math.random() > 0.5 ? 'gem' : 'star',
        collected: false, glow: Math.random() * Math.PI * 2,
      });
    }
    // Scatter enemies
    for (let i = 0; i < 8; i++) {
      s.enemies.push({
        x: (Math.random() - 0.5) * 1500,
        y: (Math.random() - 0.5) * 1500,
        frame: Math.random() * 100,
        patrolAngle: Math.random() * Math.PI * 2,
        patrolRadius: 50 + Math.random() * 80,
        originX: (Math.random() - 0.5) * 1500,
        originY: (Math.random() - 0.5) * 1500,
      });
    }

    const keys = {};
    const onKeyDown = (e) => { keys[e.key] = true; };
    const onKeyUp = (e) => { keys[e.key] = false; };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    const moveSpeed = 3;
    window.gameActions = {
      moveUp: () => { s.camY -= moveSpeed * 5; s.playerDir = 3; s.idle = false; },
      moveDown: () => { s.camY += moveSpeed * 5; s.playerDir = 0; s.idle = false; },
      moveLeft: () => { s.camX -= moveSpeed * 5; s.playerDir = 1; s.idle = false; },
      moveRight: () => { s.camX += moveSpeed * 5; s.playerDir = 2; s.idle = false; },
      shoot: () => {
        // Interact - collect nearby items
        s.items.forEach(item => {
          if (item.collected) return;
          const dx = item.x - s.camX;
          const dy = item.y - s.camY;
          if (Math.sqrt(dx * dx + dy * dy) < 80) {
            item.collected = true;
            s.score += item.type === 'gem' ? 50 : 25;
            for (let i = 0; i < 8; i++) {
              s.particles.push({
                x: width / 2 + dx, y: height / 2 + dy,
                vx: (Math.random() - 0.5) * 6, vy: (Math.random() - 0.5) * 6,
                life: 30, color: item.type === 'gem' ? '#0ff' : '#ff0',
              });
            }
          }
        });
      },
    };

    const charSize = Math.min(width * 0.08, 60);

    const loop = () => {
      s.frame++;
      const images = imagesRef.current;
      ctx.clearRect(0, 0, width, height);

      if (s.gameOver) {
        if (images.bg) ctx.drawImage(images.bg, 0, 0, width, height);
        ctx.fillStyle = 'rgba(0,0,0,0.7)'; ctx.fillRect(0, 0, width, height);
        if (onGameOver) onGameOver(Math.floor(s.score));
        return;
      }

      // Input
      s.idle = true;
      if (keys['ArrowUp'] || keys['w']) { s.camY -= moveSpeed; s.playerDir = 3; s.idle = false; s.walkAnim += 3; }
      if (keys['ArrowDown'] || keys['s']) { s.camY += moveSpeed; s.playerDir = 0; s.idle = false; s.walkAnim += 3; }
      if (keys['ArrowLeft'] || keys['a']) { s.camX -= moveSpeed; s.playerDir = 1; s.idle = false; s.walkAnim += 3; }
      if (keys['ArrowRight'] || keys['d']) { s.camX += moveSpeed; s.playerDir = 2; s.idle = false; s.walkAnim += 3; }

      if (s.invincible > 0) s.invincible--;
      if (onScoreUpdate) onScoreUpdate(Math.floor(s.score));

      // Update enemies (patrol in circles, chase if near)
      s.enemies.forEach(e => {
        e.frame++;
        e.patrolAngle += 0.02;
        const targetX = e.originX + Math.cos(e.patrolAngle) * e.patrolRadius;
        const targetY = e.originY + Math.sin(e.patrolAngle) * e.patrolRadius;
        // Chase player if close
        const dx = s.camX - e.x;
        const dy = s.camY - e.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 200) {
          e.x += (dx / dist) * 1.5;
          e.y += (dy / dist) * 1.5;
        } else {
          e.x += (targetX - e.x) * 0.02;
          e.y += (targetY - e.y) * 0.02;
        }
        // Collision with player
        if (dist < 30 && s.invincible <= 0) {
          s.lives--;
          s.invincible = 60;
          if (s.lives <= 0) s.gameOver = true;
        }
      });

      // === DRAW ===
      // Background (tiled, scrolls with camera)
      if (images.bg) {
        const bgW = width; const bgH = height;
        const offX = (-s.camX % bgW + bgW) % bgW;
        const offY = (-s.camY % bgH + bgH) % bgH;
        ctx.drawImage(images.bg, offX - bgW, offY - bgH, bgW, bgH);
        ctx.drawImage(images.bg, offX, offY - bgH, bgW, bgH);
        ctx.drawImage(images.bg, offX - bgW, offY, bgW, bgH);
        ctx.drawImage(images.bg, offX, offY, bgW, bgH);
      } else {
        ctx.fillStyle = '#1a3a1a'; ctx.fillRect(0, 0, width, height);
        // Grid
        ctx.strokeStyle = 'rgba(255,255,255,0.05)';
        const gridSize = 50;
        for (let gx = (-s.camX % gridSize + gridSize) % gridSize; gx < width; gx += gridSize) {
          ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, height); ctx.stroke();
        }
        for (let gy = (-s.camY % gridSize + gridSize) % gridSize; gy < height; gy += gridSize) {
          ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(width, gy); ctx.stroke();
        }
      }

      // Draw items
      s.items.forEach(item => {
        if (item.collected) return;
        const sx = width / 2 + (item.x - s.camX);
        const sy = height / 2 + (item.y - s.camY);
        if (sx < -50 || sx > width + 50 || sy < -50 || sy > height + 50) return;
        item.glow += 0.05;
        const pulse = 1 + Math.sin(item.glow) * 0.2;
        const size = 12 * pulse;
        ctx.fillStyle = item.type === 'gem' ? '#0ff' : '#FFD700';
        ctx.shadowColor = item.type === 'gem' ? '#0ff' : '#FFD700';
        ctx.shadowBlur = 15;
        if (item.type === 'gem') {
          ctx.beginPath();
          ctx.moveTo(sx, sy - size); ctx.lineTo(sx + size * 0.7, sy);
          ctx.lineTo(sx, sy + size * 0.5); ctx.lineTo(sx - size * 0.7, sy);
          ctx.closePath(); ctx.fill();
        } else {
          ctx.beginPath(); ctx.arc(sx, sy, size * 0.6, 0, Math.PI * 2); ctx.fill();
        }
        ctx.shadowBlur = 0;
      });

      // Draw enemies
      s.enemies.forEach(e => {
        const sx = width / 2 + (e.x - s.camX);
        const sy = height / 2 + (e.y - s.camY);
        if (sx < -100 || sx > width + 100 || sy < -100 || sy > height + 100) return;
        const enSize = charSize * 0.9;
        if (images.en) {
          ctx.save();
          ctx.translate(sx, sy);
          const wobble = Math.sin(e.frame * 0.08) * 0.1;
          const bob = Math.sin(e.frame * 0.1) * 3;
          ctx.rotate(wobble);
          ctx.drawImage(images.en, -enSize / 2, -enSize + bob, enSize, enSize);
          ctx.restore();
        } else {
          ctx.fillStyle = '#f44';
          ctx.beginPath(); ctx.arc(sx, sy, enSize / 2, 0, Math.PI * 2); ctx.fill();
        }
        // Threat indicator
        const dist = Math.sqrt((e.x - s.camX) ** 2 + (e.y - s.camY) ** 2);
        if (dist < 200) {
          ctx.strokeStyle = `rgba(255,0,0,${(200 - dist) / 200 * 0.5})`;
          ctx.lineWidth = 2;
          ctx.beginPath(); ctx.arc(sx, sy, enSize * 0.7, 0, Math.PI * 2); ctx.stroke();
        }
      });

      // Draw player (center of screen, animated)
      const px = width / 2;
      const py = height / 2;
      const blink = s.invincible > 0 && Math.floor(s.frame / 4) % 2 === 0;
      if (!blink) {
        ctx.save();
        ctx.translate(px, py);
        const breathe = Math.sin(s.frame * 0.05) * 0.02;
        const bob = s.idle ? Math.sin(s.frame * 0.04) * 2 : Math.sin(s.walkAnim * 0.2) * 4;
        ctx.rotate(breathe);
        if (images.ch) {
          ctx.drawImage(images.ch, -charSize / 2, -charSize + bob, charSize, charSize);
        } else {
          ctx.fillStyle = '#0af';
          ctx.beginPath(); ctx.arc(0, -charSize / 2 + bob, charSize / 2, 0, Math.PI * 2); ctx.fill();
        }
        // Walk legs
        if (!s.idle) {
          const legOff = Math.sin(s.walkAnim * 0.2) * charSize * 0.15;
          ctx.fillStyle = 'rgba(0,0,0,0.15)';
          ctx.fillRect(-charSize * 0.15 + legOff, -3, charSize * 0.1, 5);
          ctx.fillRect(charSize * 0.05 - legOff, -3, charSize * 0.1, 5);
        }
        ctx.restore();
      }
      // Player shadow
      ctx.fillStyle = 'rgba(0,0,0,0.2)';
      ctx.beginPath(); ctx.ellipse(px, py + 3, charSize * 0.4, 6, 0, 0, Math.PI * 2); ctx.fill();

      // Particles
      s.particles = s.particles.filter(p => p.life > 0);
      s.particles.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.life--;
        ctx.globalAlpha = p.life / 30;
        ctx.fillStyle = p.color;
        ctx.fillRect(p.x - 2, p.y - 2, 4, 4);
      });
      ctx.globalAlpha = 1;

      // Minimap
      const mmSize = 80;
      const mmX = width - mmSize - 10;
      const mmY = 10;
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillRect(mmX, mmY, mmSize, mmSize);
      ctx.strokeStyle = 'rgba(255,255,255,0.3)';
      ctx.strokeRect(mmX, mmY, mmSize, mmSize);
      // Player dot
      ctx.fillStyle = '#0af';
      ctx.fillRect(mmX + mmSize / 2 - 2, mmY + mmSize / 2 - 2, 4, 4);
      // Enemy dots
      s.enemies.forEach(e => {
        const edx = (e.x - s.camX) / 2000 * mmSize;
        const edy = (e.y - s.camY) / 2000 * mmSize;
        if (Math.abs(edx) < mmSize / 2 && Math.abs(edy) < mmSize / 2) {
          ctx.fillStyle = '#f44';
          ctx.fillRect(mmX + mmSize / 2 + edx - 1, mmY + mmSize / 2 + edy - 1, 3, 3);
        }
      });
      // Item dots
      s.items.forEach(item => {
        if (item.collected) return;
        const idx = (item.x - s.camX) / 2000 * mmSize;
        const idy = (item.y - s.camY) / 2000 * mmSize;
        if (Math.abs(idx) < mmSize / 2 && Math.abs(idy) < mmSize / 2) {
          ctx.fillStyle = item.type === 'gem' ? '#0ff' : '#FFD700';
          ctx.fillRect(mmX + mmSize / 2 + idx - 1, mmY + mmSize / 2 + idy - 1, 2, 2);
        }
      });

      // HUD
      ctx.fillStyle = '#fff'; ctx.font = 'bold 14px Rajdhani, sans-serif'; ctx.textAlign = 'left';
      ctx.fillText(`ITEMS: ${s.items.filter(i => i.collected).length}/${s.items.length}`, 10, 20);
      for (let i = 0; i < s.lives; i++) { ctx.fillStyle = '#f00'; ctx.fillText('♥', 10 + i * 18, 38); }

      // Win condition
      if (s.items.every(i => i.collected)) {
        s.score += 1000;
        s.gameOver = true;
      }

      animationRef.current = requestAnimationFrame(loop);
    };

    animationRef.current = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      delete window.gameActions;
    };
  }, [isPlaying, onScoreUpdate, onGameOver]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="openworld-canvas"
      className="absolute inset-0 w-full h-full"
      style={{ touchAction: 'none' }}
    />
  );
};

export default OpenWorldMode;
